/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';
import noop from 'lodash/noop';

import StateMachineFactory from '../state-machine/state-machine-factory';

/**
 * @import { ViewerFrame } from '../viewer/frame'
 * @import ScheduleInfo from '../schedule/schedule-info'
 */

/**
 * Rám pro zobrazování média,
 * který provádí skrývání pomocí pozicování rámu mimo obrazovku.
 * @implements {ViewerFrame}
 */
export default class PositionFrame {
  /**
   * Vytvoří instanci.
   */
  constructor() {
    const el = document.createElement('div');
    el.classList.add('position-frame');
    this.el = el;
    /** @type {ScheduleInfo | null} */
    this.scheduleInfo = null;
    this.stateMachine = new StateMachineFactory().create({
      init: 'released',
      transitions: [
        { name: 'enter', from: 'seized', to: 'entered' },
        { name: 'leave', from: ['seized', 'entered'], to: 'left' },
        { name: 'release', from: ['left', 'seized'], to: 'released' },
        {
          name: 'repair',
          from: ['entered', 'left', 'released', 'seized'],
          to: 'released'
        },
        { name: 'seize', from: 'released', to: 'seized' }
      ]
    });
  }

  /** @type {ViewerFrame['enter']} */
  enter() {
    return this.stateMachine.transit('enter', () => {
      const { scheduleInfo } = this;
      if (scheduleInfo === null) {
        return;
      }
      Object.assign(this.el.style, {
        left: '0',
        top: '0'
      });
    });
  }

  /** @type {ViewerFrame['getEl']} */
  getEl() {
    return this.el;
  }

  /**
   * Skryje obalující element tak, aby nebyl na obrazovce vidět.
   * Toho se dosáhne tak, že se se element přesune mimo obsah elementu,
   * vzhledem ke kterému je pozicován.
   * Element se přesune o nejmenší nutnou vzdálenost mimo obsah,
   * na kterou je to nutné, touto vzdáleností jsou rozměry elementu.
   * Použití záporných hodnot zajistí, že se nezobrazí posuvník.
   * Spoléhá se na to, že element ke kterému se pozicuje obsah ořízne.
   * @param {ScheduleInfo | null} scheduleInfo
   * @private
   * @returns {undefined}
   */
  hide(scheduleInfo) {
    if (scheduleInfo === null) {
      return;
    }
    Object.assign(this.el.style, {
      // Použití vw a vh jednotek, aby byla jistota,
      // že se element skryje mimo viditelnou zónu.
      left: '-100vw',
      top: '-100vh'
    });
  }

  /** @type {ViewerFrame['leave']} */
  leave() {
    return this.stateMachine.transit('leave', () => {
      this.hide(this.scheduleInfo);
    });
  }

  /** @type {ViewerFrame['release']} */
  release() {
    // Zatím se žádná operace neprovádí.
    // Do budoucna možno libovolně reagovat (přepozicování, zrušení hodnot v instanci, ...).
    // Skrytý element si zachovává styly dle posledního zobrazeného média.
    // Díky tomu se nemusí nic přepočítávat a ušetří se výkon.
    // Zároveň pokud bude mít následující médium stejné rozměry, tak se nemusí měnit nic.
    return this.stateMachine.transit('release', noop);
  }

  /** @type {ViewerFrame['repair']} */
  repair() {
    return this.stateMachine.transit('repair', () => {
      const { scheduleInfo } = this;
      if (!isNil(scheduleInfo)) {
        // Skryje se element, pokud již nějaký byl přiřazen.
        this.hide(scheduleInfo);
      }
    });
  }

  /** @type {ViewerFrame['seize']} */
  seize(scheduleInfo) {
    return this.stateMachine.transit('seize', () => {
      this.scheduleInfo = scheduleInfo;
      // Zajistí se skrytí elementu, které je závislé na rozměrech média.
      // TODO #295 Zde může teoreticky na chvíli dojít k zobrazení.
      // - Za předpokladu, že byl předchozí slajd menší, tak bude zobrazen před změnou rozměrů.
      // - Pravděpodobně by se museli použít odlišné algoritmy dle rozměrů.
      // - Pozor i na z změnu z portrait do lanscape
      //   (widthA > widthB && heightA < heightB).
      // - Pozor na pořadí, ve kterém se přiřazují CSS hodnoty.
      this.hide(scheduleInfo);
      // Nastavení rozměrů a pozadí elementu, co nejdříve to jde.
      // Díky tomu se médium preloaduje ve stavu v jakém bude zobrazeno.
      // Tím se minimaliuzují nutné změny a tím i rychlost zobrazení.
      Object.assign(this.el.style, {
        backgroundColor: scheduleInfo.getBackgroundColor(),
        height: '100%',
        width: '100%',
        zIndex: '0'
      });
    });
  }
}
